import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useRolodexSignedURLs } from 'features/rolodex'
import {
  $getNodeByKey,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const ImageContainer = styled.div<{ $isEditable?: boolean }>`
  position: relative;
  display: inline-block;
  max-width: 100%;
  user-select: none;
`

const StyledImage = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
`

const ResizeHandle = styled.div`
  position: absolute;
  bottom: -0.25rem;
  right: -0.25rem;
  background: ${({ theme }) => theme.forecast.primary || '#1890ff'};
  border: 0.0625rem solid white;
  border-radius: 0.125rem;
  width: 0.625rem;
  height: 0.625rem;
  cursor: se-resize;
  opacity: 0;
  transition: opacity 0.2s;

  ${ImageContainer}:hover & {
    opacity: 1;
  }
`

const ResizePreview = styled.div<{
  $width: number
  $height: number
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  border: 0.125rem dashed ${({ theme }) => theme.forecast.primary};
  background: ${({ theme }) => theme.forecast.primary}10;
  pointer-events: none;
  z-index: 5;
  box-sizing: border-box;
`

const LoadingPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12.5rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
`

function convertImageElement(domNode: Node): DOMConversionOutput | null {
  if (domNode instanceof HTMLElement) {
    const entryId = domNode.getAttribute('data-entry-id')
    const imagePath = domNode.getAttribute('data-image-path')
    const altText = domNode.getAttribute('data-alt-text') || ''
    const widthAttr = domNode.getAttribute('data-width')
    const heightAttr = domNode.getAttribute('data-height')

    if (entryId && imagePath) {
      const node = $createImageNode({
        entryId,
        imagePath,
        altText,
        width: widthAttr ? parseInt(widthAttr, 10) : undefined,
        height: heightAttr ? parseInt(heightAttr, 10) : undefined,
      })
      return { node }
    }
  }
  return null
}

export type SerializedImageNode = Spread<
  {
    entryId: string
    imagePath: string
    altText: string
    width?: number
    height?: number
    type: 'image'
    version: 1
  },
  SerializedLexicalNode
>

export interface ImagePayload {
  entryId: string
  imagePath: string
  altText: string
  width?: 'inherit' | number
  height?: 'inherit' | number
  key?: NodeKey
}

export class ImageNode extends DecoratorNode<React.JSX.Element> {
  __entryId: string
  __imagePath: string
  __altText: string
  __width?: 'inherit' | number
  __height?: 'inherit' | number

  static getType(): string {
    return 'image'
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      {
        entryId: node.__entryId,
        imagePath: node.__imagePath,
        altText: node.__altText,
        width: node.__width,
        height: node.__height,
      },
      node.__key
    )
  }

  constructor(payload: ImagePayload, key?: NodeKey) {
    super(key)
    this.__entryId = payload.entryId
    this.__imagePath = payload.imagePath
    this.__altText = payload.altText
    this.__width = payload.width
    this.__height = payload.height
  }

  exportJSON(): SerializedImageNode {
    return {
      entryId: this.__entryId,
      imagePath: this.__imagePath,
      altText: this.__altText,
      width: this.__width === 'inherit' ? undefined : this.__width,
      height: this.__height === 'inherit' ? undefined : this.__height,
      type: 'image',
      version: 1,
    }
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    return new ImageNode({
      entryId: serializedNode.entryId,
      imagePath: serializedNode.imagePath,
      altText: serializedNode.altText,
      width: serializedNode.width,
      height: serializedNode.height,
    })
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('embedded-image')
    element.setAttribute('data-entry-id', this.__entryId)
    element.setAttribute('data-image-path', this.__imagePath)
    element.setAttribute('data-alt-text', this.__altText)
    if (this.__width && this.__width !== 'inherit') {
      element.setAttribute('data-width', this.__width.toString())
    }
    if (this.__height && this.__height !== 'inherit') {
      element.setAttribute('data-height', this.__height.toString())
    }
    element.textContent = this.__altText || '[Image]'
    return { element }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      'embedded-image': () => ({
        conversion: convertImageElement,
        priority: 1,
      }),
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    }
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span')
    span.style.display = 'inline-block'
    return span
  }

  updateDOM(): false {
    return false
  }

  getEntryId(): string {
    return this.__entryId
  }

  getImagePath(): string {
    return this.__imagePath
  }

  getAltText(): string {
    return this.__altText
  }

  setWidthAndHeight(width: number, height: number): void {
    const writable = this.getWritable()
    writable.__width = width
    writable.__height = height
  }

  decorate(): React.JSX.Element {
    return (
      <ImageComponent
        nodeKey={this.__key}
        entryId={this.__entryId}
        imagePath={this.__imagePath}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
      />
    )
  }

  getTextContent(): string {
    return this.__altText || '[Image]'
  }

  isInline(): boolean {
    return false
  }

  isKeyboardSelectable(): boolean {
    return true
  }
}

interface ImageComponentProps {
  nodeKey: NodeKey
  entryId: string
  imagePath: string
  altText: string
  width?: 'inherit' | number
  height?: 'inherit' | number
}

function ImageComponentInner({
  nodeKey,
  imageUrl,
  altText,
  width,
  height,
}: {
  nodeKey: NodeKey
  imageUrl: string
  altText: string
  width?: 'inherit' | number
  height?: 'inherit' | number
}): React.JSX.Element {
  const [editor] = useLexicalComposerContext()
  const isEditable = editor.isEditable()
  const imageRef = useRef<HTMLImageElement>(null)
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null
  )
  const [previewSize, setPreviewSize] = useState<{
    width: number
    height: number
  } | null>(null)
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartRef = useRef<{
    startX: number
    startY: number
    startWidth: number
    startHeight: number
    aspectRatio: number
  } | null>(null)

  // Initialize size from image or props
  useEffect(() => {
    if (imageRef.current && !size) {
      const img = imageRef.current
      const onLoad = () => {
        if (width && width !== 'inherit' && height && height !== 'inherit') {
          setSize({ width: width as number, height: height as number })
        } else if (img.naturalWidth && img.naturalHeight) {
          setSize({ width: img.naturalWidth, height: img.naturalHeight })
        }
      }

      if (img.complete) {
        onLoad()
      } else {
        img.addEventListener('load', onLoad)
        return () => img.removeEventListener('load', onLoad)
      }
    }
  }, [imageRef, width, height, size])

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (!size) return

      setIsResizing(true)
      resizeStartRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startWidth: size.width,
        startHeight: size.height,
        aspectRatio: size.width / size.height,
      }
    },
    [size]
  )

  useEffect(() => {
    if (!isResizing || !resizeStartRef.current) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current) return

      const { startX, startY, startWidth, startHeight, aspectRatio } =
        resizeStartRef.current
      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY

      let newWidth: number
      let newHeight: number

      const horizontalChange = Math.abs(deltaX / startWidth)
      const verticalChange = Math.abs(deltaY / startHeight)

      if (horizontalChange > verticalChange) {
        newWidth = Math.max(100, startWidth + deltaX)
        newHeight = newWidth / aspectRatio
      } else {
        newHeight = Math.max(100 / aspectRatio, startHeight + deltaY)
        newWidth = newHeight * aspectRatio
      }

      setPreviewSize({
        width: Math.round(newWidth),
        height: Math.round(newHeight),
      })
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      resizeStartRef.current = null

      if (previewSize) {
        setSize(previewSize)
        editor.update(() => {
          const node = $getNodeByKey(nodeKey)
          if ($isImageNode(node)) {
            node.setWidthAndHeight(previewSize.width, previewSize.height)
          }
        })
      }

      setPreviewSize(null)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, editor, nodeKey, previewSize])

  return (
    <ImageContainer $isEditable={isEditable}>
      <StyledImage
        ref={imageRef}
        src={imageUrl}
        alt={altText}
        style={{
          width: size ? `${size.width}px` : width || 'inherit',
          height: size ? `${size.height}px` : height || 'inherit',
        }}
        draggable={false}
      />
      {isEditable && size && (
        <>
          {isResizing && previewSize && (
            <ResizePreview
              $width={previewSize.width}
              $height={previewSize.height}
            />
          )}
          <ResizeHandle onMouseDown={handleResizeStart} />
        </>
      )}
    </ImageContainer>
  )
}

function ImageComponent({
  nodeKey,
  entryId,
  imagePath,
  altText,
  width,
  height,
}: ImageComponentProps): React.JSX.Element {
  const { url: imageUrl, isLoading } = useRolodexSignedURLs(
    entryId && imagePath
      ? {
          entry_id: entryId,
          path: imagePath,
        }
      : undefined
  )

  if (!imageUrl) {
    return isLoading ? (
      <LoadingPlaceholder>Loading image...</LoadingPlaceholder>
    ) : (
      <LoadingPlaceholder>Failed to load image</LoadingPlaceholder>
    )
  }

  return (
    <ImageComponentInner
      nodeKey={nodeKey}
      imageUrl={imageUrl}
      altText={altText}
      width={width}
      height={height}
    />
  )
}

export function $createImageNode(payload: ImagePayload): ImageNode {
  return new ImageNode(payload)
}

export function $isImageNode(
  node: LexicalNode | null | undefined
): node is ImageNode {
  return node instanceof ImageNode
}
